import { useMetricTypes } from "src/business-utils/metrics/metricTypes.hooks";
import { TIME_WINDOW_ALL_TIME } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.constants";
import type { AssetTreeResponse } from "src/queries/assets/assetsTree.types";
import { makeQuery } from "src/queries/makeQuery";
import { useQueryPipelines } from "src/queries/pipelines/pipeline.query";
import type { PipelineMinimal } from "src/queries/pipelines/pipeline.types";
import type { CompareBodyParams } from "./ComparativeAnalysisPage.types";
import { validateBodyParams } from "./ComparativeAnalysisPage.utils";
import {
  useGetComparedTasks,
  useSearchParamsGroups,
} from "./components/ComparativeAnalysisForm/hooks/useComparativeAnalysisSearchParams";

// utilizes already-cached react-query data to get 2 lists of pipelines (for 2 envs)
// and return 2 matching ids for a provided arguments of 2 pipelines' names

interface PipelinesIdsParams {
  env1?: string;
  env2?: string;
  p1Name?: string;
  p2Name?: string;
}

export const usePipelinesIds = (
  params?: PipelinesIdsParams
): {
  p1Id: number | undefined;
  p2Id: number | undefined;
  isFetched: boolean;
  isPending: boolean;
} => {
  let { env1, env2, p1Name, p2Name } = params || {};
  const { group1, group2 } = useSearchParamsGroups();
  const maxTimeWindow = TIME_WINDOW_ALL_TIME;

  // initialize the envs with the selected ones from the atoms (if not specified)
  env1 ||= group1.env;
  env2 ||= group2.env;

  // if no pipelines ids arguments were specified, use the selected ones from the groups
  p1Name ||= group1.pipeline;
  p2Name ||= group2.pipeline;

  const p1Query = useQueryPipelines<PipelineMinimal[]>({
    enabled: !!env1,
    params: { env_name: env1, timeWindow: maxTimeWindow, minimal: true },
  });

  const p2Query = useQueryPipelines<PipelineMinimal[]>({
    enabled: !!env2,
    params: { env_name: env2, timeWindow: maxTimeWindow, minimal: true },
  });

  const p1Id = p1Query.data?.find((p) => p.app_name === p1Name)?.app_id;
  const p2Id = p2Query.data?.find((p) => p.app_name === p2Name)?.app_id;

  const isFetched =
    (!p1Query.isEnabled || p1Query.isFetched) &&
    (!p2Query.isEnabled || p2Query.isFetched);

  return {
    p1Id,
    p2Id,
    isFetched,
    isPending: p1Query.isPending || p2Query.isPending,
  };
};

//-----------------------------------------------------------

export const useGetCommonBodyParams = () => {
  const { group1, group2 } = useSearchParamsGroups();

  const body: CompareBodyParams = {
    // base params:
    env1: group1?.env,
    app_name1: group1?.pipeline,
    pit1: group1?.pit,

    // compare params:
    env2: group2?.env,
    app_name2: group2?.pipeline,
    pit2: group2?.pit,
  };

  const [[, task_id1], [, task_id2]] = useGetComparedTasks();

  // add "task_id" params to the request's body only if both available
  if (task_id1 && task_id2) {
    body.task_id1 = task_id1?.toString();
    body.task_id2 = task_id2?.toString();
  }

  return {
    body,
  };
};

//-----------------------------------------------------------

const useFetchComparativeAssetsDataBase = makeQuery<
  AssetTreeResponse<Record<string, never>>
>("/api/compare/asset-tree");

export const useFetchComparativeAssetsData = () => {
  const { body } = useGetCommonBodyParams();

  return useFetchComparativeAssetsDataBase({
    params: { ...body },
    enabled: validateBodyParams(body),
  });
};

//-----------------------------------------------------------

const useFetchComparativeMetricsDataBase = makeQuery("/api/compare/metrics");

export const useFetchComparativeMetricsData = <T extends Record<string, any>>(
  transformer?: (data: T[]) => T[]
) => {
  const metricTypes = useMetricTypes();
  const { body } = useGetCommonBodyParams();

  return useFetchComparativeMetricsDataBase({
    params: { ...body },
    enabled: validateBodyParams(body) && !!metricTypes,
    select: (data: unknown) => {
      try {
        const dataArray = data as T[];
        const mappedMetrics = dataArray.map((m: T) => ({
          ...(metricTypes?.[m.metric_type as keyof typeof metricTypes] ?? {}),
          ...m,
          table_grouping: undefined,
        }));

        // conditionally transform the data using a custom transformer
        return transformer?.(mappedMetrics) || mappedMetrics;
      } catch (err) {
        console.error(err);
        throw new Error("Data transformation failed");
      }
    },
  });
};

//-----------------------------------------------------------

export interface LineageEdge {
  output_ds: string;
  input_ds: string;
  input_columns: string;
}

export interface LineageNode {
  ds_io_type?: string;
  asset_type: string;
  node_type: string;
  group_name?: string;
  group_type?: string | null;
  input_columns?: string[];
}

interface LineageGraph {
  edges: LineageEdge[];
  nodes: Record<string, LineageNode>;
}

export interface CompareLineageResponse {
  data: LineageGraph;
  execution: LineageGraph;
}

const useQueryCompareLineageBase = makeQuery<CompareLineageResponse[]>(
  "/api/compare/lineage"
);

export const useQueryCompareLineage = () => {
  const { body } = useGetCommonBodyParams();

  return useQueryCompareLineageBase({
    params: { ...body },
    enabled: Object.values(body).every((n) => n),
  });
};
